import techsJson from './generated/techs.json' with { type: 'json' };
import equipmentJson from './generated/equipment.json' with { type: 'json' };
import specialistsJson from './generated/specialists.json' with { type: 'json' };
import towersJson from './generated/towers.json' with { type: 'json' };
import unitsJson from './generated/units.json' with { type: 'json' };
const techs = techsJson.techs;
const equipment = equipmentJson.equipment;
const specialists = specialistsJson.specialists;
const towerSkills = towersJson.towerSkills;
const unitTypesDefault = unitsJson.units;
/**
 * Effects of one technology for the fight: `effects[]` as they are, plus every `perRank` term as
 * `value x rank - value` (a rank-scaled effect and a flat correction), so that a squad at rank r receives
 * exactly `value x (r - 1)` — "60 block, +60 per further rank" gives 60 x r in total.
 * Techs whose numbers the sim's named ability applies itself are filtered by the sim (techChannelsOwnedByAbility).
 */
export function techFightEffects(t) {
    const out = t.effects.map((e) => ({ channel: e.channel, value: e.value, layer: 'tech' }));
    for (const e of t.perRank ?? []) {
        out.push({ channel: e.channel, value: e.value, layer: 'tech', scalesWithLevel: true });
        out.push({ channel: e.channel, value: -e.value, layer: 'tech' });
    }
    return out;
}
/** `FightInput.techEffects`: every captured technology with at least one channel, by tech id. */
export const TECH_EFFECTS = (() => {
    const out = {};
    for (const t of techs) {
        const eff = techFightEffects(t);
        if (eff.length > 0)
            out[t.id] = eff;
    }
    return out;
})();
/** `FightInput.equipmentEffects`: every equipment item with at least one channel, by equipment id. */
export const EQUIPMENT_EFFECTS = (() => {
    const out = {};
    for (const e of equipment)
        if (e.effects.length > 0)
            out[e.id] = e.effects.map((x) => ({ channel: x.channel, value: x.value, layer: 'equipment' }));
    return out;
})();
/** The two id-keyed tables every FightInput should carry (client, server, smoke, crosscheck). */
export function fightEffectTables() {
    return { techEffects: TECH_EFFECTS, equipmentEffects: EQUIPMENT_EFFECTS };
}
/** Whether a side-wide modifier scope covers a unit type ('ranged' = every non-melee weapon, 'ground' / 'air' by isFly). */
export function scopeMatches(scope, def) {
    if (scope === 'all')
        return true;
    if (scope === 'ranged')
        return def.weapon !== 'melee';
    if (scope === 'ground')
        return !def.isFly;
    if (scope === 'air')
        return def.isFly;
    return scope.includes(def.id);
}
/** Fight-side modifiers of a specialist (Fortified +17 % HP, Cost Control −11 % / −11 %, Aerial +13 % on fliers, Speed +3). */
export function specialistModifiers(id) {
    return specialists.find((s) => s.id === id)?.modifiers ?? [];
}
/** Fight-side modifiers of a tower skill (Enhanced Range on ranged units, High Mobility, Attack / Defense Enhancement I–II). */
export function towerSkillModifiers(tower, id) {
    return towerSkills.find((s) => s.tower === tower && s.id === id)?.modifiers ?? [];
}
/**
 * Splits side-wide scoped modifiers into the sim's two carriers: the `all` scope goes to
 * `FightInput.seatEffects[seat]`, restricted scopes are attached per squad as `SquadPlacement.effects`
 * (layer 'seat'). Returns a new deployment (the input is not mutated) and the seat-wide list.
 */
export function applySeatModifiers(deployment, scoped, unitTypes = unitTypesDefault) {
    const seatEffects = [];
    const restricted = [];
    for (const s of scoped) {
        if (s.scope === 'all')
            for (const e of s.effects)
                seatEffects.push({ channel: e.channel, value: e.value, layer: 'seat' });
        else
            restricted.push(s);
    }
    if (restricted.length === 0)
        return { deployment, seatEffects };
    const squads = deployment.squads.map((sq) => {
        const def = unitTypes[sq.typeId];
        if (!def)
            return sq;
        const extra = [];
        for (const s of restricted)
            if (scopeMatches(s.scope, def))
                for (const e of s.effects)
                    extra.push({ channel: e.channel, value: e.value, layer: 'seat' });
        if (extra.length === 0)
            return sq;
        return { ...sq, effects: [...(sq.effects ?? []), ...extra] };
    });
    return { deployment: { ...deployment, squads }, seatEffects };
}
//# sourceMappingURL=fightEffects.js.map